<template>
  <div class="search-box" v-click-outside="onClickOutside">
    <app-input
      v-model="inputValue"
      :placeholder="$t('header.filters.searchPlaceholder')"
      @focus="onFocus"
      :class="{ 'search-box__input--no-items': hasNoResults }"
    >
      <template #icon>
        <app-icon icon="magnifier" class="search-box__icon" />
      </template>
    </app-input>

    <div v-if="isSearchActive" class="search-box__dropdown">
      <button
        v-for="(result, index) in searchResults"
        :key="`result-${index}`"
        class="search-box__dropdown-item"
        @click="selectWord(result)"
      >
        {{ getTextFromHtmlString(result) }}
      </button>
    </div>
  </div>
</template>

<script>
import AppInput from "@/components/common/AppInput/AppInput";
import { mapState, mapActions, mapGetters } from "vuex";
import getTextFromHtmlString from "@/helpers/getTextFromHtmlString";

export default {
  name: "SearchField",
  components: { AppInput },
  data() {
    return {
      inputValue: "",
      isInFocus: false,
      searchResults: []
    };
  },
  computed: {
    ...mapState("Filters", ["searchQuery"]),
    ...mapGetters("Coordinates", ["images"]),
    isSearchActive() {
      return this.inputValue.length > 2 && this.isInFocus;
    },
    hasNoResults() {
      return this.isSearchActive && this.searchResults.length === 0;
    }
  },
  watch: {
    inputValue(newValue) {
      this.searchResults = this.getSearchResults();
      if (newValue === "" && this.searchQuery !== "") {
        this.setSearchQuery("");
        this.reloadCoordinates();
      }
    },
    searchQuery(newQuery) {
      if (newQuery !== this.inputValue) {
        this.inputValue = newQuery;
      }
    }
  },
  methods: {
    ...mapActions("Filters", ["setSearchQuery"]),
    ...mapActions("Coordinates", ["reloadCoordinates"]),
    onFocus() {
      this.isInFocus = true;
    },
    onClickOutside() {
      this.isInFocus = false;
    },
    selectWord(word) {
      this.inputValue = word;
      this.setSearchQuery(word);
      this.isInFocus = false;
      this.reloadCoordinates();
    },
    getTextFromHtmlString,
    getSearchResults() {
      if (!this.isSearchActive) return [];

      const keywordMap = new Map();
      const inputLower = this.inputValue.toLowerCase();
      const hasHyphen = inputLower.includes("-");

      this.images.forEach((img) => {
        const company = getTextFromHtmlString(img.company || "");
        const description = getTextFromHtmlString(img.description || "");
        const fullText = `${company} ${description}`;

        // -----------------------------
        // CASE 1: user typed hyphen
        // -----------------------------
        if (hasHyphen) {
          // extract ALL hyphen phrases
          const matches = fullText.matchAll(
            /[\p{L}\p{N}]+(?:\s*-\s*[\p{L}\p{N}]+)+/gu
          );

          for (const match of matches) {
            const phrase = match[0];
            const normalizedPhrase = phrase
              .toLowerCase()
              .replace(/\s*-\s*/g, "-");

            const normalizedInput = inputLower.replace(/\s*-\s*/g, "-");

            //  strict match: phrase must include input
            if (normalizedPhrase.includes(normalizedInput)) {
              keywordMap.set(phrase, phrase);
            }
          }

          return;
        }

        // -----------------------------
        // CASE 2: normal word search
        // -----------------------------
        const words = fullText.split(/\s*-\s*|\s+/);

        words.forEach((word) => {
          const cleanedWord = word.replace(/[^\p{L}\p{N}]/gu, "");
          if (!cleanedWord) return;

          if (cleanedWord.toLowerCase().includes(inputLower)) {
            keywordMap.set(cleanedWord.toLowerCase(), cleanedWord);
          }
        });
      });

      return Array.from(keywordMap.values());
    }


  }
};
</script>

<style lang="scss">
.search-box {
  position: relative;
  display: flex;
  margin: 0 9px;

  &__input {
    &--no-items {
      color: $input-title-color;
    }
  }

  &__icon {
    margin-top: 5px;
  }

  &__dropdown {
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateY(100%);
    display: flex;
    flex-direction: column;
    min-width: 100%;
    max-height: 70vh;
    overflow: auto;
    padding: 10px 0;
    background-color: $input-background-color;

    &:before {
      content: "";
      position: absolute;
      top: 0;
      right: 10px;
      left: 11px;
      display: block;
      height: 1px;
      background-color: $input-placeholder-color;
    }

  }

  &__dropdown-item {
    padding: 9px 10px;
    color: $app-primary-color;
    font-weight: 400;
    background-color: rgba($app-primary-color, 0);
    transition: background-color .2s;
    border: none;
    text-align: left;
    font-size: 1em;
    cursor: pointer;

    &:hover {
      background-color: rgba($app-primary-color, .1);
    }

  }

}
</style>
